import React from 'react';
import { HeatmapData, getRetentionColor } from '../services/cohortAnalyticsService';

export interface CohortRetentionHeatmapProps {
  heatmapData: HeatmapData;
}

const CohortRetentionHeatmap: React.FC<CohortRetentionHeatmapProps> = ({ heatmapData }) => {
  const { cohortLabels, periodLabels, heatmapData: matrix } = heatmapData;

  // Helper to format cell value
  const formatCellValue = (value: number): string => {
    return value.toFixed(1) + '%';
  };

  // Helper to get text color based on background
  const getTextColor = (value: number): string => {
    return value >= 60 ? '#ffffff' : '#1f2937';
  };

  return (
    <div className="w-full overflow-x-auto bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
      <table className="min-w-full border-collapse text-sm">
        <thead>
          <tr>
            <th className="sticky left-0 bg-gray-50 dark:bg-gray-800 z-10 p-3 text-left font-semibold border border-gray-300 dark:border-gray-600">
              Cohort
            </th>
            {periodLabels.map((period) => (
              <th
                key={period}
                className="p-3 text-center font-semibold border border-gray-300 dark:border-gray-600 min-w-[60px]"
              >
                P{period}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {cohortLabels.map((cohortLabel, cohortIndex) => (
            <tr key={cohortIndex} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
              <td className="sticky left-0 bg-white dark:bg-gray-900 z-10 p-3 border border-gray-300 dark:border-gray-600 font-medium whitespace-nowrap">
                {cohortLabel}
              </td>
              {periodLabels.map((period, periodIndex) => {
                const value = matrix[cohortIndex]?.[periodIndex] ?? 0;
                const bgColor = getRetentionColor(value);
                const textColor = getTextColor(value);

                return (
                  <td
                    key={period}
                    className="p-0 border border-gray-300 dark:border-gray-600"
                    style={{ backgroundColor: bgColor }}
                    title={`Cohort: ${cohortLabel}, Period ${period}: ${formatCellValue(value)}`}
                  >
                    <div
                      className="p-3 text-center text-xs font-semibold"
                      style={{ color: textColor }}
                    >
                      {formatCellValue(value)}
                    </div>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>

      {/* Legend */}
      <div className="flex items-center gap-3 mt-4 flex-wrap">
        <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Retention Scale:</span>
        {[
          { min: 80, label: '80-100%', color: '#10b981' },
          { min: 60, label: '60-79%', color: '#3b82f6' },
          { min: 40, label: '40-59%', color: '#f59e0b' },
          { min: 20, label: '20-39%', color: '#ef4444' },
          { min: 0, label: '0-19%', color: '#991b1b' },
        ].map((item) => (
          <div key={item.min} className="flex items-center gap-1.5">
            <div
              className="w-5 h-5 rounded border border-gray-300"
              style={{ backgroundColor: item.color }}
            />
            <span className="text-xs text-gray-600 dark:text-gray-400">{item.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CohortRetentionHeatmap;
